// external libs
import { Box, Dialog, DialogContent, DialogTitle, makeStyles, Typography } from '@material-ui/core'
import { Form } from 'react-final-form'

// components
import Autocomplete from '../../../../control/autocomplete/autocomplete'
import Button from '../../../../control/button/button'

// hooks
import useTranslate from '../../../../i18n/useTranslate'
import useProject from '../../../../services/web/useProject'
import useSnackbarAction from '../../../../stores/snackbar/snackbarAction'

// types
import type { TOptions } from '../../../../control/autocomplete/autocompleteType'
import type { RevertOption, RevertStatusRequest } from '../../../../services/web/useProject'

export interface RevertProjectStatusDialogProps {
    projectId: number
    isOpen: boolean
    revertOptions: RevertOption[]
    onClose?: () => void
    onSubmitted?: () => void
}

const RevertProjectStatusDialog = (props: RevertProjectStatusDialogProps) => {
    const { projectId, isOpen, revertOptions, onClose, onSubmitted } = props
    const classes = useStyles()
    const snackbarAction = useSnackbarAction()
    const translate = useTranslate()
    const currentLanguage = translate.languageStore.currentLanguage
    const projectServices = useProject()

    const options: TOptions[] = (revertOptions ?? []).map((e) => ({
        label: e.stepName[currentLanguage as keyof typeof e.stepName],
        value: `${e.stepID}`,
    }))

    const onSubmit = async (values: any) => {
        try {
            const revertStatusRequest: RevertStatusRequest = {
                stepID: Number(values.stepID?.value),
            }
            const { status } = await projectServices.revertStatus(projectId, revertStatusRequest)

            if (status !== 1) {
                throw new Error('has an error while revert project status')
            }

            snackbarAction.openSnackbar({
                message: 'ย้อนสถานะโครงการสำเร็จ',
                type: 'success',
            })

            onSubmitted && onSubmitted()
        } catch (error) {
            snackbarAction.openSnackbar({
                message: 'ย้อนสถานะโครงการไม่สำเร็จ',
                type: 'error',
            })
        }
    }

    return (
        <Dialog open={isOpen} fullWidth maxWidth="sm" className={classes.dialog} onClose={onClose}>
            <DialogTitle className={classes.dialogTitle}>
                <Typography variant="h2" className={classes.title}>
                    ย้อนกลับสถานะโครงการ
                </Typography>
            </DialogTitle>
            <DialogContent className={classes.dialogContent}>
                <Form onSubmit={onSubmit} initialValues={{}}>
                    {({ handleSubmit, values }) => {
                        return (
                            <Box>
                                <Typography style={{ marginBottom: 16 }}>
                                    เลือกสถานะที่ต้องการย้อนกลับไปสำหรับโครงการนี้
                                </Typography>
                                <Autocomplete
                                    name="stepID"
                                    materialInputProps={{
                                        label: 'ย้อนกลับไปที่สถานะ: ',
                                        placeholder: 'เลือกสถานะ',
                                    }}
                                    options={options}
                                    required
                                />

                                <Box display="flex" justifyContent="center" style={{ gap: 16, marginTop: 32 }}>
                                    <Button
                                        variant="contained"
                                        color="inherit"
                                        className={classes.cancelButton}
                                        onClick={onClose}
                                    >
                                        {translate.get('BUTTON.CANCEL')}
                                    </Button>

                                    <Button
                                        variant="contained"
                                        color="primary"
                                        disabled={!values.stepID}
                                        onClick={handleSubmit}
                                    >
                                        {translate.get('BUTTON.CONFIRM')}
                                    </Button>
                                </Box>
                            </Box>
                        )
                    }}
                </Form>
            </DialogContent>
        </Dialog>
    )
}

const useStyles = makeStyles((theme) => ({
    dialog: {
        '& .MuiPaper-root': {
            borderRadius: 8,
        },
    },

    dialogTitle: {
        color: theme.palette.common.white,
        backgroundColor: theme.palette.primary.main,
    },

    dialogContent: {
        padding: 24,
    },

    title: {
        fontSize: 16,
        fontWeight: 700,
    },

    cancelButton: { backgroundColor: '#FFFFFF', border: 'solid 1px #db5f8e' },
}))

export default RevertProjectStatusDialog
